import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, FileText } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi, type ThesisPrintRequest, type ThesisPrintStatus } from "@/lib/api/bindery";
import { ThesisStatusBadge } from "@/components/bindery/BinderyStatusBadge";
import { ThesisStatusStepper } from "@/components/bindery/ThesisStatusStepper";
import {
  BINDING_TYPE_LABELS,
  DEGREE_LEVEL_LABELS,
  formatDate,
  formatMoney,
} from "../binderyFormatters";
import { StaffPaymentCard } from "./StaffPaymentCard";

const NEXT_ACTIONS: {
  label: string;
  status: ThesisPrintStatus;
  allowedFrom: ThesisPrintStatus[];
  variant?: "default" | "outline" | "destructive";
}[] = [
  { label: "Start format check", status: "FORMAT_CHECK", allowedFrom: ["SUBMITTED"] },
  { label: "Approve", status: "APPROVED", allowedFrom: ["FORMAT_CHECK", "SUBMITTED"] },
  { label: "Start printing", status: "PRINTING", allowedFrom: ["APPROVED"] },
  { label: "Send to binding", status: "BINDING", allowedFrom: ["PRINTING"] },
  { label: "Mark ready for pickup", status: "READY", allowedFrom: ["BINDING", "PRINTING"] },
  { label: "Mark collected", status: "COLLECTED", allowedFrom: ["READY"] },
  {
    label: "Reject",
    status: "REJECTED",
    allowedFrom: ["SUBMITTED", "FORMAT_CHECK"],
    variant: "destructive",
  },
];

export default function ThesisRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [req, setReq] = useState<ThesisPrintRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingStatus, setPendingStatus] = useState<ThesisPrintStatus | null>(null);
  const [costOverride, setCostOverride] = useState<string>("");
  const [rejectionReason, setRejectionReason] = useState("");

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await binderyApi.thesis.getById(id);
      setReq(data);
      setCostOverride(String(data.cost));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to load thesis request"));
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    reload();
  }, [reload]);

  const transition = async (status: ThesisPrintStatus) => {
    if (!req) return;
    const payload: { status: ThesisPrintStatus; rejectionReason?: string; cost?: number } = {
      status,
    };
    if (status === "REJECTED") {
      if (!rejectionReason.trim()) {
        toast.error("Please provide a rejection reason.");
        return;
      }
      payload.rejectionReason = rejectionReason.trim();
    }
    const parsedCost = Number(costOverride);
    if (!Number.isNaN(parsedCost) && parsedCost !== Number(req.cost)) {
      payload.cost = parsedCost;
    }
    setPendingStatus(status);
    try {
      const next = await binderyApi.thesis.review(req.id, payload);
      setReq(next);
      toast.success(`Moved to ${status.replace(/_/g, " ")}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update thesis request"));
    } finally {
      setPendingStatus(null);
    }
  };

  if (loading || !req) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={req.requestNumber}
        description={req.title}
        breadcrumbItems={[
          { label: "Thesis requests", href: "/bindery/thesis" },
          { label: req.title },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <Card>
        <CardContent className="pt-6">
          <ThesisStatusStepper status={req.status} />
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-4">
        <InfoTile label="Status" value={<ThesisStatusBadge status={req.status} />} />
        <InfoTile
          label="Patron"
          value={
            [req.patron.firstName, req.patron.lastName].filter(Boolean).join(" ") ||
            req.patron.email
          }
        />
        <InfoTile label="Degree" value={DEGREE_LEVEL_LABELS[req.degreeLevel]} />
        <InfoTile label="Binding" value={BINDING_TYPE_LABELS[req.bindingType]} />
        <InfoTile label="Pages × Copies" value={`${req.pageCount} × ${req.copies}`} />
        <InfoTile label="Color pages" value={String(req.colorPages)} />
        <InfoTile label="Submitted" value={formatDate(req.submittedAt)} />
        <InfoTile label="Cost" value={formatMoney(req.cost)} />
      </div>

      {req.supervisor && <InfoRow label="Supervisor" value={req.supervisor} />}
      {req.department?.name && <InfoRow label="Department" value={req.department.name} />}
      {req.formattingNotes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Formatting notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{req.formattingNotes}</p>
          </CardContent>
        </Card>
      )}

      {req.fileUrl && (
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <FileText className="h-4 w-4 text-slate-500" />
              Manuscript file uploaded
            </div>
            <a
              href={req.fileUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-primary hover:underline"
            >
              Download
            </a>
          </CardContent>
        </Card>
      )}

      <StaffPaymentCard req={req} onChange={setReq} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Staff actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor="cost">Override final cost</Label>
              <Input
                id="cost"
                type="number"
                value={costOverride}
                onChange={(e) => setCostOverride(e.target.value)}
                step="0.01"
                min="0"
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="reason">Rejection reason (if rejecting)</Label>
              <Textarea
                id="reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={2}
                placeholder="Shown to student if request is rejected"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {NEXT_ACTIONS.filter((a) => a.allowedFrom.includes(req.status))
              .filter((a) => !(a.status === "PRINTING" && !req.paidAt))
              .map((a) => (
                <Button
                  key={a.status}
                  variant={a.variant ?? "default"}
                  disabled={pendingStatus !== null}
                  onClick={() => transition(a.status)}
                >
                  {pendingStatus === a.status ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {a.label}
                </Button>
              ))}
            {req.status === "APPROVED" && !req.paidAt && (
              <span className="text-xs text-slate-500">
                Record the student's payment above to enable printing.
              </span>
            )}
            {req.status === "SUBMITTED" || req.status === "FORMAT_CHECK" ? null : (
              <span className="text-xs text-slate-500">
                No further transitions available from this state.
              </span>
            )}
          </div>

          {req.rejectionReason && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <span className="font-semibold">Previously rejected:</span> {req.rejectionReason}
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

function InfoTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs text-slate-500">{label}</p>
      <div className="mt-1 text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white px-4 py-3 md:flex-row md:items-center md:justify-between">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900">{value}</span>
    </div>
  );
}
